import { LitElement } from 'lit';

export class InvertedAccordionItem extends LitElement {
  static properties = {
    title: { type: String },
    open: { type: Boolean, reflect: true },
    accordionStyle: { type: String, attribute: 'accordion-style' }
  };

  createRenderRoot() {
    return this; // Use light DOM instead of shadow DOM
  }

  constructor() {
    super();
    this.title = '';
    this.open = false;
    this.accordionStyle = 'inverted';
    this.timeline = null;
    this.isDesktop = window.matchMedia('(min-width: 768px)').matches;
  }

  connectedCallback() {
    super.connectedCallback();
    
    // Find the button that PHP generated and add click listener
    const button = this.querySelector('.accordion-item-header');
    if (button) {
      button.addEventListener('click', () => this.toggle());
    }
    
    // Listen for resize events to update isDesktop
    this.mediaQueryList = window.matchMedia('(min-width: 768px)');
    this.handleMediaChange = (e) => {
      this.isDesktop = e.matches;
      this.initializeState();
    };
    this.mediaQueryList.addEventListener('change', this.handleMediaChange);
    
    // Initialize state after DOM is ready
    setTimeout(() => this.initializeState(), 0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.mediaQueryList && this.handleMediaChange) {
      this.mediaQueryList.removeEventListener('change', this.handleMediaChange);
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === 'open' && oldValue !== null) {
      this.initializeState();
    }
  }

  initializeState() {
    const content = this.querySelector('.accordion-item-content');
    const icon = this.querySelector('.accordion-icon');
    const title = this.querySelector('.accordion-item-title');

    if (!content || !icon || !title) {
      console.log('InvertedAccordionItem: Missing elements', { content: !!content, icon: !!icon, title: !!title });
      return;
    }

    // Update isDesktop state
    this.isDesktop = window.matchMedia('(min-width: 768px)').matches;

    // Set initial CSS - responsive width based on screen size
    content.style.width = this.isDesktop ? '50%' : '100%';
    content.style.position = 'relative';

    if (window.gsap) {
      // Create the animation timeline (but don't play it yet)
      const naturalHeight = content.scrollHeight || 'auto';
      
      if (this.isDesktop) {
        // Desktop: use slide animation
        this.timeline = window.gsap.timeline({ paused: true })
          .to(content, { 
            height: naturalHeight,
            x: '100%', 
            duration: 0.4, 
            ease: 'power2.out' 
          })
          .to(title, {
            x: '100%',  // Slide title right by its own width (50% of container)
            duration: 0.4,
            ease: 'power2.out'
          }, 0)  // Start at same time as content
          .to(content, { 
            opacity: 1, 
            duration: 0.3, 
            ease: 'power2.out' 
          }, 0.1)
          .to(icon, { 
            rotation: 45, 
            duration: 0.2, 
            ease: 'power2.out' 
          }, 0);
      } else {
        // Mobile: simple accordion without slide animation
        this.timeline = window.gsap.timeline({ paused: true })
          .to(content, { 
            height: naturalHeight,
            duration: 0.3, 
            ease: 'power2.out' 
          })
          .to(content, { 
            opacity: 1, 
            duration: 0.3, 
            ease: 'power2.out' 
          }, 0)
          .to(icon, { 
            rotation: 45, 
            duration: 0.2, 
            ease: 'power2.out' 
          }, 0);
      }

      // Set initial state based on open property
      if (!this.open) {
        window.gsap.set(content, { x: 0, height: 0, opacity: 0 });
        window.gsap.set(title, { x: 0 }); // Title at left
        window.gsap.set(icon, { rotation: 0 });
      } else {
        // For initially open items, play timeline to end
        this.timeline.progress(1);
        // The timeline tweened height to a one-time scrollHeight snapshot,
        // which may have been measured before images finished loading.
        // Release it back to plain CSS 'auto' so the box can keep growing.
        content.style.height = 'auto';
        content.style.overflow = 'visible';
      }
    } else {
      // Fallback for non-GSAP browsers
      if (!this.open) {
        content.style.transform = 'translateX(0)';
        content.style.height = '0px';
        content.style.opacity = '0';
        content.style.overflow = 'hidden';
        title.style.transform = 'translateX(0)';
        icon.style.transform = 'rotate(0deg)';
      } else {
        if (this.isDesktop) {
          content.style.transform = 'translateX(100%)';
          title.style.transform = 'translateX(100%)';
        } else {
          content.style.transform = 'translateX(0)';
          title.style.transform = 'translateX(0)';
        }
        content.style.height = 'auto';
        content.style.opacity = '1';
        content.style.overflow = 'visible';
        icon.style.transform = 'rotate(45deg)';
      }
    }
  }

  toggle() {
    this.open = !this.open;
    this.animateContent();
  }

  animateContent() {
    const content = this.querySelector('.accordion-item-content');
    const icon = this.querySelector('.accordion-icon');
    const title = this.querySelector('.accordion-item-title');

    if (!content || !icon || !title) return;

    // Update isDesktop state
    this.isDesktop = window.matchMedia('(min-width: 768px)').matches;

    if (window.gsap && this.timeline) {
      // Recreate timeline if screen size changed
      const naturalHeight = content.scrollHeight;
      
      if (this.isDesktop) {
        // Desktop: use slide animation
        this.timeline = window.gsap.timeline({ paused: true })
          .to(content, { 
            height: naturalHeight,
            x: '100%', 
            duration: 0.4, 
            ease: 'power2.out' 
          })
          .to(title, {
            x: '100%',
            duration: 0.4,
            ease: 'power2.out'
          }, 0)
          .to(content, { 
            opacity: 1, 
            duration: 0.3, 
            ease: 'power2.out' 
          }, 0.1)
          .to(icon, { 
            rotation: 45, 
            duration: 0.2, 
            ease: 'power2.out' 
          }, 0);
      } else {
        // Mobile: simple accordion without slide animation
        this.timeline = window.gsap.timeline({ paused: true })
          .to(content, { 
            height: naturalHeight,
            duration: 0.3, 
            ease: 'power2.out' 
          })
          .to(content, { 
            opacity: 1, 
            duration: 0.3, 
            ease: 'power2.out' 
          }, 0)
          .to(icon, { 
            rotation: 45, 
            duration: 0.2, 
            ease: 'power2.out' 
          }, 0);
      }
      
      // Play forward for open, reverse for close
      if (this.open) {
        // Content (e.g. lazy-loading images) may still grow after the
        // tween ends. Release the pixel height back to 'auto' so the box
        // keeps expanding instead of clipping at the snapshot height.
        this.timeline.eventCallback('onComplete', () => {
          content.style.height = 'auto';
          content.style.overflow = 'visible';
        });
        this.timeline.play();
      } else {
        this.timeline.reverse();
      }
    } else {
      // Fallback: Use CSS transitions
      content.style.transition = 'transform 0.4s ease, opacity 0.3s ease, height 0.4s ease';
      title.style.transition = 'transform 0.4s ease';
      icon.style.transition = 'transform 0.2s ease';

      if (this.open) {
        // First get the natural height
        content.style.height = 'auto';
        const naturalHeight = content.scrollHeight;
        
        // Set it back to current state then animate
        content.style.height = '0px';
        content.style.overflow = 'hidden';
        
        // Force reflow
        content.offsetHeight;
        
        // Now animate to open
        content.style.height = naturalHeight + 'px';
        content.style.opacity = '1';
        icon.style.transform = 'rotate(45deg)';
        
        if (this.isDesktop) {
          content.style.transform = 'translateX(100%)';
          title.style.transform = 'translateX(100%)';
        } else {
          content.style.transform = 'translateX(0)';
          title.style.transform = 'translateX(0)';
        }
        
        // After animation, set height to auto for responsive behavior
        setTimeout(() => {
          content.style.height = 'auto';
          content.style.overflow = 'visible';
        }, 400);
      } else {
        // Get current height first
        const currentHeight = content.scrollHeight;
        content.style.height = currentHeight + 'px';
        content.style.overflow = 'hidden';
        
        // Force reflow
        content.offsetHeight;
        
        // Now animate to closed
        content.style.height = '0px';
        content.style.transform = 'translateX(0)';
        content.style.opacity = '0';
        title.style.transform = 'translateX(0)';
        icon.style.transform = 'rotate(0deg)';
      }
      
      // Remove transitions after animation to prevent issues
      setTimeout(() => {
        content.style.transition = '';
        title.style.transition = '';
        icon.style.transition = '';
      }, 450);
    }
  }
}